"use client";

import React, { useState } from "react";
import { FaCheckCircle, FaClock, FaBolt, FaStar } from "react-icons/fa";
import { FiCopy, FiCheck } from "react-icons/fi";
import Jazzicon, { jsNumberForAddress } from "react-jazzicon";
import styles from "./TaskCardList.module.css";
import { Task } from "./types";
import {
  getTaskId,
  truncateHash,
  formatPayment,
  formatTimeRemaining,
  getStatusConfig,
} from "./utils";

interface TaskCardProps {
  task: Task;
  isNew: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, isNew }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const isExpired = task.expiry_time < Date.now();
  const expiryLabel = isExpired ? "Expired" : "Expires in";
  const statusConfig = getStatusConfig(task.status);
  const taskId = getTaskId(task._id);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div
      key={taskId}
      className={styles.card}
      style={{
        opacity: isNew ? 0 : 1,
        animation: isNew ? "fadeIn 0.25s ease-in-out forwards" : undefined,
        transition: "all 0.3s ease-in-out",
      }}
    >
      <div className={styles.cardHeader}>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {task.source_info?.image_content?.logo_url && (
            <img
              src={task.source_info.image_content.logo_url}
              alt={task.source_info.image_content.name}
              style={{
                width: "1.25rem",
                height: "1.25rem",
                borderRadius: "0.25rem",
                objectFit: "cover",
                background: "#ffffff",
                padding: "1px",
              }}
            />
          )}
          <a
            href={`https://management.opengpu.network/sources/${task.source}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.sourceLink}
          >
            {task.source_info?.image_content?.name || "OpenGPU Activity"}
          </a>
          <span className={`${styles.badge} ${statusConfig.colorClass}`} style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
            {task.status === "New" ? <FaStar size={10} /> :
             task.status === "Attempted" ? <FaBolt size={10} /> :
             (task.status === "Completed" || task.status === "Responded" || task.status === "Finalized") ? <FaCheckCircle size={10} /> :
             <FaClock size={10} />}
            {statusConfig.text}
          </span>
        </div>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardRow} style={{ marginBottom: "0.75rem" }}>
          <span style={{ flexShrink: 0, display: "inline-flex" }}>
            <Jazzicon diameter={16} seed={jsNumberForAddress(task.address)} />
          </span>
          <a href={`/address/${task.address}`} className={styles.cardTitle}>
            {truncateHash(task.address)}
          </a>
          <button
            className={styles.copyButton}
            onClick={() => copyToClipboard(task.address, "address")}
          >
            {copiedField === "address" ? <FiCheck size={12} /> : <FiCopy size={12} />}
          </button>
        </div>
        <div
          className={styles.cardRow}
          style={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "flex-start" }}
        >
          <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
            <span className={styles.cardLabel} style={{ minWidth: "auto" }}>Payment</span>
            <span className={styles.cardValue}>
              {formatPayment(String(task.offered_reward ?? "0"))} OGPU
            </span>
          </div>
          <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
            <span className={styles.cardLabel} style={{ minWidth: "auto" }}>{expiryLabel}</span>
            <span className={styles.cardValue}>
              {isExpired
                ? `${formatTimeRemaining(task.expiry_time)} ago`
                : formatTimeRemaining(task.expiry_time)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
